import { EServiceChannel, ETypeServicePackage } from "@base/enums";
import { parseExtraInfo } from "@base/helpers/extraInfo";
import { IDeliveryAddress } from "@base/modules/customerDeliveryAddress/customerDeliveryAddress.interface";
import { IPackageFacility } from "@base/modules/doctor/doctorExtraInfo";
import { IAppointment, IAppointmentUsedService } from "./appointment.interface";

/** One filled-in "additional information" field as stored on the appointment. */
export interface IExtraFieldDefinition {
  field_name?: string;
  field_type?: string;
  required?: unknown;
  label?: Record<string, string> | string;
  value?: string;
}

/** Configuration data must not take a page down, so anything unparseable reads as absent. */
const parseJson = <T>(raw?: string): T | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" ? (parsed as T) : undefined;
  } catch {
    return undefined;
  }
};

export interface IAppointmentUsedServiceView {
  used?: IAppointmentUsedService;
  /** The partner clinic the service is delivered at, when there is one. */
  facility?: IPackageFacility;
  /** The patient's own address, when the service is delivered at home. */
  customerAddress?: IDeliveryAddress;
  /** Only the fields the patient actually filled in. */
  filledExtraFields: IExtraFieldDefinition[];
  /** Set on an appointment booked from a package; gates the contact phone and the cancel limit. */
  isFromPackage: boolean;
  maxCancelTimes?: string;
  checkoutSurvey?: string;
}

/**
 * Everything the appointment screens need out of `appointment_used_services[0]` - the only element
 * the apps read. Half of its fields are JSON encoded as a string, so they are parsed once here
 * rather than at each render site.
 */
export const getAppointmentUsedService = (appointment?: IAppointment): IAppointmentUsedServiceView => {
  const used = appointment?.appointment_used_services?.[0];
  const extraInfo = parseExtraInfo(used?.extra_info);
  // Booked from the web or the apps the clinic is its bare address; older bookings stored the clinic as JSON.
  const facility =
    parseJson<IPackageFacility>(used?.facility_address) ?? (used?.facility_address?.trim() ? { address: used.facility_address.trim() } : undefined);
  const customerAddress = parseJson<IDeliveryAddress>(used?.customer_address);

  const extraFields = parseJson<IExtraFieldDefinition[]>(used?.extra_fields);

  return {
    used,
    facility,
    customerAddress,
    filledExtraFields: Array.isArray(extraFields) ? extraFields.filter((field) => !!String(field.value ?? "").trim()) : [],
    // Insurance bookings carry the implement but not always the package type.
    isFromPackage: used?.package_type === "PACKAGE" || !!used?.patient_special_package_detail_implement_id,
    maxCancelTimes: typeof extraInfo.max_appointment_cancel_times === "string" ? extraInfo.max_appointment_cancel_times : undefined,
    checkoutSurvey: typeof extraInfo.checkout_survey === "string" ? extraInfo.checkout_survey : undefined,
  };
};

/**
 * Where the service actually happens replaces the booking site: a partner clinic or the patient's
 * home tells them where to be, while the selling site does not.
 */
export const hasServiceLocation = (view: IAppointmentUsedServiceView) => !!view.facility || !!view.customerAddress;

/**
 * Where the booking actually landed wins over the line's declared type: a "home or clinic" line
 * becomes one or the other once the patient picks a channel, and the saved address says which.
 * Appointments without a `service_type` are not package bookings and keep the plain online/offline tag.
 */
export const getServiceChannel = (view: IAppointmentUsedServiceView): EServiceChannel | undefined => {
  const serviceType = view.used?.service_type;

  if (!serviceType) {
    return undefined;
  }

  if (view.customerAddress) {
    return EServiceChannel.HOME;
  }

  if (view.facility) {
    return EServiceChannel.FACILITY;
  }

  switch (serviceType) {
    case ETypeServicePackage.HOMECARE_SERVICE:
      return EServiceChannel.HOME;
    case ETypeServicePackage.OFFLINE_SERVICE:
      return EServiceChannel.FACILITY;
    case ETypeServicePackage.ONLINE_SERVICE:
      return EServiceChannel.ONLINE;
    case ETypeServicePackage.HOMECARE_OR_OFFLINE_SERVICE:
      return EServiceChannel.HOME_OR_FACILITY;
    default:
      return undefined;
  }
};
